import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import Form from '../../../common/Form';
import { useForm, Controller } from 'react-hook-form';
import TextArea from '../../../common/Form/TextArea';
import { hardCloseServiceRequest } from '../../../actions/service_request';
import { MilestoneSubStatus } from '../TicketController';
import { MilestoneSubStatusColor } from '../TicketColumns/Status';
import IconToolTip from '../../../common/IconToolTip';

const formName = 'hardClose';

export default function HardCloseTicket({ id, milestones, agents }) {
    const { formButtonLoading } = useSelector((state) => state.loading);
    const dispatch = useDispatch();

    // Prepare milestone options
    const mileStonesOption = useMemo(() => {
        return milestones.map((elem) => ({
            key: elem.id,
            label: elem.type,
            value: elem.id,
            milestone_status: elem.milestone_status,
        }));
    }, [milestones]);

    // Default: all open milestones checked, or none if none open
    const defaultMilestone = useMemo(() => {
        const openMile = mileStonesOption.filter(
            (elem) => elem.milestone_status === MilestoneSubStatus.NEW
        );
        if (openMile.length > 0) {
            return openMile.map((m) => m.value);
        }
        return [];
    }, [mileStonesOption]);

    const defaultValues = {
        job_sign_off_notes: '',
        milestone: defaultMilestone,
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    useEffect(() => {
        methods.setValue('milestone', defaultMilestone);
    }, [defaultMilestone, methods]);

    const submit = (formValues) => {
        const payload = {
            ticketId: id,
            remark: formValues?.job_sign_off_notes,
            closed_at: new Date().toISOString(),
            formName,
            milestone_ids: formValues.milestone,
        };
        dispatch(hardCloseServiceRequest(payload));
    };

    const getAgentTooltip = (milestoneId) => {
        if (!agents || !Array.isArray(agents)) return null;
        const sp = agents.find((a) => a.role === 'SP' && a.milestone_id === milestoneId);
        const fe = agents.find((a) => a.role === 'FE' && a.milestone_id === milestoneId);
        const spName = sp?.agent?.display_name || sp?.agent?.full_name;
        const feName = fe?.agent?.display_name || fe?.agent?.full_name;
        if (!sp && !fe) {
            return <span>SP/FE: <strong>Not Assigned</strong></span>;
        }
        if (sp && fe && sp.agent?.id === fe.agent?.id) {
            return <span>Current SP/FE: <strong>{spName}</strong></span>;
        }
        return (
            <span>
                {sp ? <>Current SP: <strong>{spName}</strong></> : <>SP: <strong>Not Assigned</strong></>}
                <br />
                {fe ? <>Current FE: <strong>{feName}</strong></> : <>FE: <strong>Not Assigned</strong></>}
            </span>
        );
    };

    // Custom checkbox UI
    const renderCheckbox = (option, field) => {
        const checked = Array.isArray(field.value) && field.value.includes(option.value);
        const handleChange = () => {
            let newValue = Array.isArray(field.value) ? [...field.value] : [];
            if (checked) {
                newValue = newValue.filter((v) => v !== option.value);
            } else {
                newValue.push(option.value);
            }
            field.onChange(newValue);
        };
        const tooltip = getAgentTooltip(option.value);
        const statusClass = MilestoneSubStatusColor[option.milestone_status] || '';
        const checkboxLabel = (
            <button
                key={option.value}
                type="button"
                onClick={handleChange}
                className={`flex items-center gap-2
                    rounded-lg text-left transition-all duration-200 ease-in-out border
                    focus:ring-offset-2
                    ${checked
                        ? 'bg-scogoprimary text-white shadow-md outline-none border-scogoprimary border-l-4'
                        : `${statusClass} hover:bg-opacity-80 hover:shadow-sm`
                    } py-2 px-4 !text-sm !font-medium`}
            >
                <div className="flex-shrink-0 flex items-center">
                    {checked ? (
                        <span className="material-icons text-[16px] leading-none">check_circle</span>
                    ) : (
                        <span className="material-icons text-[16px] leading-none opacity-40">radio_button_unchecked</span>
                    )}
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="capitalize">{option.label}</span>
                    {option.milestone_status && (
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm ${checked ? 'bg-white/20 text-white' : 'opacity-80'}`}>
                            {option.milestone_status.replace(/_/g, ' ')}
                        </span>
                    )}
                </div>
            </button>
        );
        if (tooltip) {
            return <IconToolTip key={option.value} title={tooltip}>{checkboxLabel}</IconToolTip>;
        }
        return checkboxLabel;
    };

    return (
        <>
            <Form
                methods={methods}
                onSubmit={submit}
                formName={formName}
                buttonPostion={'justify-end'}
                submitButtonText={'Submit'}
                onCancel={() => dispatch(closeModalAction())}
                showSubmit
                submitContainerClassName='flex gap-2 justify-end py-4 px-4'
                isLoading={formButtonLoading[formName]}
            >
                <div className='px-2 pt-2'>
                    <div className="mb-4 px-2">
                        <div className="font-semibold mb-2">Select Milestone(s) to Close</div>
                        <Controller
                            name="milestone"
                            control={methods.control}
                            rules={{
                                validate: (value) =>
                                    Array.isArray(value) && value.length > 0
                                        ? true
                                        : 'Please select at least one milestone',
                            }}
                            render={({ field }) => (
                                <div>
                                    <div className='flex flex-wrap gap-3'>
                                        {mileStonesOption.map((option) => renderCheckbox(option, field))}
                                    </div>
                                    {methods.formState.errors.milestone && (
                                        <div className="text-red-500 text-xs mt-1">
                                            {methods.formState.errors.milestone.message}
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                    <TextArea
                        name='job_sign_off_notes'
                        className='w-full'
                        label='Reason'
                        height='4rem'
                        required
                    />
                </div>
            </Form>
        </>
    );
}
